import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { packagingApi } from 'api/packaging';

function getDownloadErrorMessage(documentLabel, message) {
    const fallbackMessage = `Failed to download ${documentLabel}.`;
    if (!message) {
        return fallbackMessage;
    }

    const normalizedMessage = String(message).trim();
    if (normalizedMessage.toLowerCase().startsWith(fallbackMessage.toLowerCase())) {
        return normalizedMessage;
    }

    return `${fallbackMessage} ${normalizedMessage}`;
}

/**
 * Shared packaging PO actions for list and entity views.
 * Caller provides refetch and (for duplicate) history + getFiltersForBackNav.
 *
 * @param {Object} options
 * @param {() => Promise<unknown>} [options.refetch] - Called after successful send/cancel.
 * @param {Object} [options.history] - React Router history (required for duplicate navigation).
 * @param {() => string} [options.getFiltersForBackNav] - Returns current filters string for duplicate/create back state.
 */
export function usePackagingPOActions({ getFiltersForBackNav, history, refetch }) {
    const [selectedPurchaseOrderForCancel, setSelectedPurchaseOrderForCancel] = useState(null);
    const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
    const [isSubmittingCancel, setIsSubmittingCancel] = useState(false);

    const openCancelDialog = useCallback((rowOrPo) => {
        setSelectedPurchaseOrderForCancel(rowOrPo ?? null);
        setIsCancelDialogOpen(true);
    }, []);

    const closeCancelDialog = useCallback(() => {
        if (isSubmittingCancel) return;
        setIsCancelDialogOpen(false);
        setSelectedPurchaseOrderForCancel(null);
    }, [isSubmittingCancel]);

    const duplicate = useCallback(
        (rowOrPoId) => {
            const poId = rowOrPoId?.id ?? rowOrPoId;
            if (poId == null || !history) return;

            const filters = typeof getFiltersForBackNav === 'function' ? getFiltersForBackNav() : '';

            history.push({
                pathname: '/packaging/createPurchaseOrder-v2',
                search: `?duplicateFrom=${encodeURIComponent(poId)}`,
                state: {
                    packagingPurchaseOrdersFilters: filters,
                    purchaseOrdersFilters: filters,
                },
            });
        },
        [getFiltersForBackNav, history],
    );

    const downloadPdf = useCallback(async (rowOrPoId, vendorName) => {
        const poId = rowOrPoId?.id ?? rowOrPoId;
        if (poId == null) return;

        try {
            const response = await packagingApi.downloadPackagingPOPDF(poId, vendorName ?? rowOrPoId?.vendorName);
            if (response?.status === false) {
                toast.error(getDownloadErrorMessage('PDF', response?.msg));
            }
        } catch (downloadError) {
            toast.error(getDownloadErrorMessage('PDF', downloadError?.message));
        }
    }, []);

    const downloadShipList = useCallback(async (rowOrPoId) => {
        const poId = rowOrPoId?.id ?? rowOrPoId;
        if (poId == null) return;

        try {
            const response = await packagingApi.downloadPackagingPOShipList(poId);
            if (response?.status === false) {
                toast.error(getDownloadErrorMessage('Ship-List', response?.msg));
            }
        } catch (downloadError) {
            toast.error(getDownloadErrorMessage('Ship-List', downloadError?.message));
        }
    }, []);

    const sendToVendor = useCallback(
        async (rowOrPoId) => {
            const poId = rowOrPoId?.id ?? rowOrPoId;
            if (poId == null) return;

            try {
                const response = await packagingApi.sendPurchaseOrderToVendor(poId);
                if (!response?.status) {
                    const message = response?.msg ? `Failed to send PO. ${response.msg}` : 'Failed to send PO.';
                    toast.error(message);
                    return;
                }
                toast.success('Sent Packaging PO to Vendor.');
                await refetch?.();
            } catch (sendError) {
                toast.error(`Failed to send PO. ${sendError?.message ?? ''}`.trim());
            }
        },
        [refetch],
    );

    const confirmCancel = useCallback(
        async (reasonCode) => {
            const poId = selectedPurchaseOrderForCancel?.id;
            if (!poId || !reasonCode || isSubmittingCancel) return;

            setIsSubmittingCancel(true);
            try {
                const response = await packagingApi.cancelPurchaseOrder(poId, reasonCode);
                if (!response?.status) {
                    const message = response?.msg ? `Failed to cancel PO ${response.msg}` : 'Failed to cancel PO';
                    toast.error(message);
                    return;
                }
                toast.success('Purchase Order cancelled.');
                setIsCancelDialogOpen(false);
                setSelectedPurchaseOrderForCancel(null);
                await refetch?.();
            } catch (cancelError) {
                toast.error(`Failed to cancel PO. ${cancelError?.message ?? ''}`.trim());
            } finally {
                setIsSubmittingCancel(false);
            }
        },
        [isSubmittingCancel, refetch, selectedPurchaseOrderForCancel?.id],
    );

    return {
        cancelDialog: {
            close: closeCancelDialog,
            isOpen: isCancelDialogOpen,
            open: openCancelDialog,
        },
        cancelState: {
            isSubmitting: isSubmittingCancel,
        },
        confirmCancel,
        downloadPdf,
        downloadShipList,
        duplicate,
        sendToVendor,
    };
}

usePackagingPOActions.displayName = 'usePackagingPOActions';
